import type { EventEmitter } from '@stencil/core';
import { Component, Element, Event, Host, Prop, State, Watch, h } from '@stencil/core';
import type { HTMLStencilElement } from '@stencil/core/internal';
import type { HTMLAttributes } from 'react';
import type { Components as SyComponents } from '../../components';
import type { PageEvent } from './types';

@Component({
  tag: 'sy-paginator',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class Paginator {
  @Element() el: HTMLSyPaginatorElement;

  /**
   * Sets active page, this will define which button appears active.
   */
  @Prop({ reflect: true, mutable: true }) index = 1;

  /**
   * Sets the number of available pages to navigation.
   */
  @Prop({ reflect: true }) length = 1;

  /**
   * If true, hide first and last page direct buttons.
   */
  @Prop({ reflect: true }) hideFirstAndLastButtons = false;

  /**
   * If true, hide previous and next page direct buttons.
   */
  @Prop({ reflect: true }) hidePreviousAndNextButtons = false;

  /**
   * Sets how many internal buttons (between the arrows buttons) will be shown.
   */
  @Prop({ reflect: true, mutable: true }) maxInternalButtons = 4;

  /**
   * Sets paginator's state to disabled.
   */
  @Prop({ reflect: true }) disabled = false;

  @State() internalButtons = this.maxInternalButtons;

  /**
   * Emitted after active page changes.
   */
  @Event() syPaginatorPageDidChange: EventEmitter<PageEvent>;

  @Watch('index')
  indexWatcher(): void {
    if (this.index < 1) this.setActivePage(1);
    else if (this.index > this.length) this.setActivePage(this.length);
  }

  @Watch('maxInternalButtons')
  maxInternalButtonsWatcher(): void {
    if (this.maxInternalButtons < 1) this.maxInternalButtons = 0;
    else if (this.maxInternalButtons < 3) this.maxInternalButtons = 3;
    else if (this.maxInternalButtons > 10) this.maxInternalButtons = 10;

    this.internalButtons = this.maxInternalButtons;
  }

  private arrowButtonsIcons = {
    first: 'first_page',
    previous: 'navigate_before',
    next: 'navigate_next',
    last: 'last_page',
  };

  private setActivePage(index: number) {
    // Index must be between 1 and length
    if (index < 1) {
      index = 1;
    } else if (index > this.length) {
      index = this.length;
    }

    // Check if index has really changed
    if (this.index !== index) {
      this.index = index;
      this.syPaginatorPageDidChange.emit({
        index: this.index,
      });
    }
  }

  private getArrowButtonAttributes(
    type: 'first' | 'previous' | 'next' | 'last',
  ): Partial<SyComponents.SyButton & HTMLAttributes<HTMLStencilElement>> {
    let attributes: Partial<SyComponents.SyButton & HTMLAttributes<HTMLStencilElement>> = {
      content: 'icon-only',
      icon: this.arrowButtonsIcons[type],
    };

    if (
      (type === 'first' && this.index === 1) ||
      (type === 'previous' && this.index === 1) ||
      (type === 'next' && this.index === this.length) ||
      (type === 'last' && this.index === this.length)
    ) {
      attributes = {
        ...attributes,
        disabled: true,
        tabIndex: -1,
        'aria-disabled': true,
      };
    }

    return attributes;
  }

  private getArrowButtonStyle(type: 'first' | 'previous' | 'next' | 'last') {
    let styles: any = {};
    if (type === 'first' || type === 'last') {
      styles = this.hideFirstAndLastButtons ? { ...styles, style: { display: 'none' } } : styles;
    } else {
      styles = this.hidePreviousAndNextButtons ? { ...styles, style: { display: 'none' } } : styles;
    }

    return styles;
  }

  private getInternalButtonAttributes(
    index: number | '...',
  ): Partial<SyComponents.SyButton | HTMLAttributes<HTMLSyButtonElement>> {
    let attributes: Partial<SyComponents.SyButton | HTMLAttributes<HTMLSyButtonElement>> = {
      content: 'text-only',
      label: index as string,
      variant: 'clear',
      color: '' as any, // it is necessary to use empty string to avoid task queue immediate errors.
    };

    if (this.index === index) {
      attributes = {
        ...attributes,
        color: 'primary',
        variant: 'filled',
        'aria-current': 'page',
      };
    } else if (index === '...') {
      attributes = {
        ...attributes,
        tabIndex: -1,
      };
    }

    return attributes;
  }

  private getInternalButtonStyle(index: number | '...') {
    let styles: any = {};

    if (this.index === index) {
      styles = {};
    } else if (index === '...') {
      styles = {
        ...styles,
        style: { 'pointer-events': 'none' },
      };
    }

    if (typeof index === 'number') {
      if (index > 999) {
        styles = { ...styles, style: { '--padding-x': '.1rem' } };
      } else if (index > 99) {
        styles = { ...styles, style: { '--padding-x': '.25rem' } };
      } else if (index > 9) {
        styles = { ...styles, style: { '--padding-x': '.5rem' } };
      }
    }

    return styles;
  }

  private renderInternalButtons() {
    if (this.internalButtons < 3) return;

    const indexes: number[] = [];
    if (this.length < this.internalButtons + 2) {
      for (let index = 1; index <= this.length; index++) {
        indexes.push(index);
      }
      return (
        <div>
          {indexes.map((index) => (
            <sy-button
              disabled={this.disabled}
              key={index}
              {...this.getInternalButtonAttributes(index)}
              {...this.getInternalButtonStyle(index)}
              onClick={() => this.setActivePage(index)}
            ></sy-button>
          ))}
        </div>
      );
    }

    if (this.index <= this.internalButtons / 2) {
      for (let index = 1; index < this.internalButtons; index++) {
        indexes.push(index);
      }
      return (
        <div>
          {indexes.map((index) => (
            <sy-button
              disabled={this.disabled}
              key={index}
              {...this.getInternalButtonAttributes(index)}
              {...this.getInternalButtonStyle(index)}
              onClick={() => this.setActivePage(index)}
            ></sy-button>
          ))}
          <sy-button
            disabled={this.disabled}
            {...this.getInternalButtonAttributes('...')}
            {...this.getInternalButtonStyle('...')}
          ></sy-button>
          <sy-button
            disabled={this.disabled}
            {...this.getInternalButtonAttributes(this.length)}
            {...this.getInternalButtonStyle(this.length)}
            onClick={() => this.setActivePage(this.length)}
          ></sy-button>
        </div>
      );
    }

    if (this.index + 1 < this.length - Math.ceil(this.internalButtons / 2)) {
      for (
        let index = this.index - Math.floor(this.internalButtons / 2) + 1;
        index < this.index + Math.ceil(this.internalButtons / 2);
        index++
      ) {
        indexes.push(index);
      }
      return (
        <div>
          {indexes.map((index) => (
            <sy-button
              disabled={this.disabled}
              key={index}
              {...this.getInternalButtonAttributes(index)}
              {...this.getInternalButtonStyle(index)}
              onClick={() => this.setActivePage(index)}
            ></sy-button>
          ))}
          <sy-button
            disabled={this.disabled}
            {...this.getInternalButtonAttributes('...')}
            {...this.getInternalButtonStyle('...')}
          ></sy-button>
          <sy-button
            disabled={this.disabled}
            {...this.getInternalButtonAttributes(this.length)}
            {...this.getInternalButtonStyle(this.length)}
            onClick={() => this.setActivePage(this.length)}
          ></sy-button>
        </div>
      );
    }

    for (let index = this.length - this.internalButtons; index <= this.length; index++) {
      indexes.push(index);
    }
    return (
      <div>
        {indexes.map((index) => (
          <sy-button
            disabled={this.disabled}
            key={index}
            {...this.getInternalButtonAttributes(index)}
            {...this.getInternalButtonStyle(index)}
            onClick={() => this.setActivePage(index)}
          ></sy-button>
        ))}
      </div>
    );
  }

  private resizePaginator() {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry?.contentRect?.width) {
          // Typical size
          const buttonSize = 40;
          const buttons = this.el.shadowRoot?.querySelectorAll('sy-button') as NodeListOf<HTMLSyButtonElement>;
          const canHasMaxButtons = Math.ceil((entry.contentRect.width - buttonSize) / buttonSize);

          let howManyArrowButtons = 4;
          howManyArrowButtons -= this.hideFirstAndLastButtons ? 2 : 0;
          howManyArrowButtons -= this.hidePreviousAndNextButtons ? 2 : 0;

          if (
            howManyArrowButtons + this.internalButtons > canHasMaxButtons ||
            buttons.length > canHasMaxButtons ||
            this.internalButtons < this.maxInternalButtons
          ) {
            let buttonsAfterResize = canHasMaxButtons - howManyArrowButtons;
            buttonsAfterResize -= this.length > buttonsAfterResize ? 1 : 0;
            buttonsAfterResize =
              buttonsAfterResize > this.maxInternalButtons ? this.maxInternalButtons : buttonsAfterResize;
            buttonsAfterResize = buttonsAfterResize < 3 ? 0 : buttonsAfterResize;
            this.internalButtons = buttonsAfterResize;
          }
        }
      }
    });

    resizeObserver.observe(this.el);
  }

  componentWillLoad() {
    this.indexWatcher();
    this.maxInternalButtonsWatcher();
    this.resizePaginator();
  }

  render() {
    return (
      <Host role="navigation" aria-label="pagination">
        <sy-button
          disabled={this.disabled}
          {...this.getArrowButtonAttributes('first')}
          {...this.getArrowButtonStyle('first')}
          onClick={() => this.setActivePage(1)}
        ></sy-button>
        <sy-button
          disabled={this.disabled}
          {...this.getArrowButtonAttributes('previous')}
          {...this.getArrowButtonStyle('previous')}
          onClick={() => this.setActivePage(this.index - 1)}
        ></sy-button>
        {this.renderInternalButtons()}
        <sy-button
          disabled={this.disabled}
          {...this.getArrowButtonAttributes('next')}
          {...this.getArrowButtonStyle('next')}
          onClick={() => this.setActivePage(this.index + 1)}
        ></sy-button>
        <sy-button
          disabled={this.disabled}
          {...this.getArrowButtonAttributes('last')}
          {...this.getArrowButtonStyle('last')}
          onClick={() => this.setActivePage(this.length)}
        ></sy-button>
      </Host>
    );
  }
}
